/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    FaSpinner,
    FaTrophy,
    FaSearch,
    FaFilter,
    FaCalendarAlt,
    FaClock,
    FaUserClock,
    FaCheckCircle,
    FaUserPlus,
    FaTimesCircle,
    FaExternalLinkAlt,
    FaMapMarkerAlt,
    FaTimes,
    FaArrowLeft,
    FaArrowRight
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_AWARDS = `${API_BASE_URL}/UIawards`;

// Dynamic Awards List Component
function AwardsGallery() {
    const [awards, setAwards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        totalResults: 0
    });

    // New state for image popup
    const [selectedAward, setSelectedAward] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [allImages, setAllImages] = useState([]);

    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    // Fetch awards
    const fetchAwards = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_AWARDS, {
                headers: { Authorization: `Bearer ${token}` },
            });

            let awardsData = [];

            if (res.data && Array.isArray(res.data.items)) {
                awardsData = res.data.items;
                // ✅ FILTER INACTIVE AWARDS
                awardsData = awardsData.filter(award => award.isActive !== false);
                setPagination({
                    page: res.data.page || 1,
                    limit: res.data.limit || 10,
                    totalPages: res.data.totalPages || 1,
                    totalResults: res.data.totalResults || 0
                });
            } else if (Array.isArray(res.data)) {
                awardsData = res.data;
                // ✅ FILTER INACTIVE AWARDS
                awardsData = awardsData.filter(award => award.isActive !== false);
            } else {
                console.warn("Unexpected API response structure:", res.data);
                awardsData = [];
            }

            // Process awards to get first image
            const awardsWithFirstImage = await Promise.all(
                awardsData.map(async (award) => {
                    const awardWithImage = { ...award };
                    awardWithImage.firstImage = null;
                    awardWithImage.allGalleryImages = []; // Store all images for popup

                    // Try to get first image from gallery
                    if (award.gallery && Array.isArray(award.gallery)) {
                        for (const group of award.gallery) {
                            if (group.photos && Array.isArray(group.photos) && group.photos.length > 0) {
                                for (const photo of group.photos) {
                                    try {
                                        const mediaId = photo.mediaId?._id || photo.mediaId?.id || photo.mediaId;
                                        if (mediaId) {
                                            const imgRes = await axios.get(
                                                `${API_BASE_URL}/UImedia/${mediaId}/download`,
                                                {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    responseType: "blob",
                                                }
                                            );
                                            if (imgRes.data) {
                                                const imageUrl = URL.createObjectURL(imgRes.data);
                                                awardWithImage.allGalleryImages.push(imageUrl);

                                                // Set first image if not set
                                                if (!awardWithImage.firstImage) {
                                                    awardWithImage.firstImage = imageUrl;
                                                }
                                            }
                                        }
                                    } catch (err) {
                                        console.error("Error loading image:", err);
                                    }
                                }
                            }
                        }
                    }

                    if (!awardWithImage.firstImage) {
                        awardWithImage.firstImage = "https://via.placeholder.com/400x200/6B7280/FFFFFF?text=No+Image+Available";
                    }

                    return awardWithImage;
                })
            );

            setAwards(awardsWithFirstImage || []);

        } catch (err) {
            console.error("Error fetching awards:", err);
            console.error("Error details:", err.response?.data || err.message);
            setAwards([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAwards();
    }, []);

    // Clean up object URLs when component unmounts
    useEffect(() => {
        return () => {
            awards.forEach(award => {
                // Clean up first image
                if (award.firstImage && award.firstImage.startsWith('blob:')) {
                    URL.revokeObjectURL(award.firstImage);
                }
                // Clean up all gallery images
                if (award.allGalleryImages && Array.isArray(award.allGalleryImages)) {
                    award.allGalleryImages.forEach(img => {
                        if (img.startsWith('blob:')) {
                            URL.revokeObjectURL(img);
                        }
                    });
                }
            });
        };
    }, [awards]);

    // Handle image click to open popup
    const handleImageClick = (award) => {
        if (award.allGalleryImages && award.allGalleryImages.length > 0) {
            setSelectedAward(award);
            setSelectedImageIndex(0);
            setAllImages(award.allGalleryImages);
            setIsPopupOpen(true);
        }
    };

    // Close popup
    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedAward(null);
        setSelectedImageIndex(0);
        setAllImages([]);
    };

    // Navigate to next image
    const nextImage = () => {
        if (allImages.length > 0) {
            setSelectedImageIndex((prevIndex) =>
                prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    // Navigate to previous image
    const prevImage = () => {
        if (allImages.length > 0) {
            setSelectedImageIndex((prevIndex) =>
                prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
            );
        }
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isPopupOpen) return;

            if (e.key === 'Escape') {
                closePopup();
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'ArrowLeft') {
                prevImage();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isPopupOpen]);

    // Handle Apply Now button click - Navigate to registration page
    const handleApplyNow = (award) => {
        const awardId = award._id || award.id;
        navigate(`/AwardRegistration/${awardId}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return "Invalid Date";
        }
    };

    // Check if award is upcoming (not started yet)
    const isAwardUpcoming = (award) => {
        if (!award.awardEventStartDate) return false;
        try {
            const awardEventStartDate = new Date(award.awardEventStartDate);
            const today = new Date();
            return awardEventStartDate > today;
        } catch (error) {
            return false;
        }
    };

    // Check if award is active (started but not ended)
    const isAwardActive = (award) => {
        if (!award.awardEventStartDate || !award.awardEventEndDate) return false;
        try {
            const awardEventStartDate = new Date(award.awardEventStartDate);
            const awardEventEndDate = new Date(award.awardEventEndDate);
            const today = new Date();
            return awardEventStartDate <= today && awardEventEndDate >= today;
        } catch (error) {
            return false;
        }
    };

    // Check if award is completed (ended)
    const isAwardCompleted = (award) => {
        if (!award.awardEventEndDate) return false;
        try {
            const awardEventEndDate = new Date(award.awardEventEndDate);
            const today = new Date();
            return awardEventEndDate < today;
        } catch (error) {
            return false;
        }
    };

    // Check if registration is open
    const isRegistrationOpen = (award) => {
        if (!award.registrationStartDate || !award.registrationEndDate) return false;
        try {
            const registrationStartDate = new Date(award.registrationStartDate);
            const registrationEndDate = new Date(award.registrationEndDate);
            const today = new Date();
            return today >= registrationStartDate && today <= registrationEndDate;
        } catch (error) {
            return false;
        }
    };

    // Check if registration hasn't started yet
    const isRegistrationNotStarted = (award) => {
        if (!award.registrationStartDate) return false;
        try {
            const registrationStartDate = new Date(award.registrationStartDate);
            const today = new Date();
            return registrationStartDate > today;
        } catch (error) {
            return false;
        }
    };

    // Check if registration is closed
    const isRegistrationClosed = (award) => {
        if (!award.registrationEndDate) return false;
        try {
            const registrationEndDate = new Date(award.registrationEndDate);
            const today = new Date();
            return registrationEndDate < today;
        } catch (error) {
            return false;
        }
    };

    // Format location display
    const formatLocation = (award) => {
        if (!award.location) return "Location not specified";

        const { venue, city, state, country } = award.location;
        const locationParts = [];

        if (venue) locationParts.push(venue);
        if (city) locationParts.push(city);
        if (state) locationParts.push(state);
        if (country) locationParts.push(country);

        return locationParts.join(', ');
    };

    // Get award status badge
    const getAwardStatus = (award) => {
        if (isAwardUpcoming(award)) {
            return {
                type: 'upcoming',
                label: 'UPCOMING',
                color: 'blue',
                icon: FaClock,
                bgColor: 'bg-blue-500',
                textColor: 'text-blue-100'
            };
        } else if (isAwardActive(award)) {
            return {
                type: 'active',
                label: 'ACTIVE',
                color: 'green',
                icon: FaUserClock,
                bgColor: 'bg-green-500',
                textColor: 'text-green-100'
            };
        } else if (isAwardCompleted(award)) {
            return {
                type: 'completed',
                label: 'COMPLETED',
                color: 'red',
                icon: FaCheckCircle,
                bgColor: 'bg-red-500',
                textColor: 'text-red-100'
            };
        } else {
            return {
                type: 'unknown',
                label: 'UNKNOWN',
                color: 'gray',
                icon: FaClock,
                bgColor: 'bg-gray-500',
                textColor: 'text-gray-100'
            };
        }
    };

    // Get registration status badge
    const getRegistrationStatus = (award) => {
        if (isRegistrationNotStarted(award)) {
            return {
                type: 'not-started',
                label: 'REGISTRATION NOT STARTED',
                color: 'gray',
                icon: FaClock,
                bgColor: 'bg-gray-100',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-300'
            };
        } else if (isRegistrationOpen(award)) {
            return {
                type: 'open',
                label: 'REGISTRATION OPEN',
                color: 'green',
                icon: FaUserPlus,
                bgColor: 'bg-green-100',
                textColor: 'text-green-800',
                borderColor: 'border-green-300'
            };
        } else if (isRegistrationClosed(award)) {
            return {
                type: 'closed',
                label: 'REGISTRATION CLOSED',
                color: 'red',
                icon: FaTimesCircle,
                bgColor: 'bg-red-100',
                textColor: 'text-red-800',
                borderColor: 'border-red-300'
            };
        } else {
            return {
                type: 'unknown',
                label: 'REGISTRATION UNKNOWN',
                color: 'gray',
                icon: FaClock,
                bgColor: 'bg-gray-100',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-300'
            };
        }
    };

    const filteredAwards = awards.filter((a) => {
        const matchesSearch =
            a.awardFullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.awardDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (a.location?.venue && a.location.venue.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (a.location?.city && a.location.city.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "upcoming" && isAwardUpcoming(a)) ||
            (statusFilter === "ongoing" && isAwardActive(a)) ||
            (statusFilter === "completed" && isAwardCompleted(a)) ||
            (statusFilter === "registration-open" && isRegistrationOpen(a)) ||
            (statusFilter === "registration-closed" && isRegistrationClosed(a)) ||
            (statusFilter === "registration-not-started" && isRegistrationNotStarted(a));

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                                <div className="w-2 h-8 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full"></div>
                                🏆 MASA Awards Gallery
                            </h1>
                            <p className="text-gray-300">Discover and explore our prestigious awards and competitions</p>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-700/50 p-4 md:p-6">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                            {/* Search Input */}
                            <div className="flex-1 w-full relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaSearch className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search awards by name, description, venue, or city..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-yellow-400/30 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-300 bg-gray-700/50 text-white placeholder-gray-400"
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="flex flex-wrap gap-2 w-full md:w-auto">
                                <button
                                    onClick={() => setStatusFilter("all")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "all"
                                            ? "bg-yellow-500 text-gray-900 shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaFilter size={12} />
                                    All
                                </button>
                                <button
                                    onClick={() => setStatusFilter("upcoming")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "upcoming"
                                            ? "bg-blue-500 text-white shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaClock size={12} />
                                    Upcoming
                                </button>
                                <button
                                    onClick={() => setStatusFilter("ongoing")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "ongoing"
                                            ? "bg-green-500 text-white shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaUserClock size={12} />
                                    Ongoing
                                </button>
                                <button
                                    onClick={() => setStatusFilter("completed")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "completed"
                                            ? "bg-red-500 text-white shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaCheckCircle size={12} />
                                    Completed
                                </button>
                                <button
                                    onClick={() => setStatusFilter("registration-open")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "registration-open"
                                            ? "bg-green-500 text-white shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaUserPlus size={12} />
                                    Reg Open
                                </button>
                                <button
                                    onClick={() => setStatusFilter("registration-closed")}
                                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${statusFilter === "registration-closed"
                                            ? "bg-red-500 text-white shadow-md"
                                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                        }`}
                                >
                                    <FaTimesCircle size={12} />
                                    Reg Closed
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Awards List */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <h2 className="text-xl font-bold text-white">Featured Awards</h2>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-300 bg-yellow-500/20 px-3 py-1 rounded-full font-medium">
                                Showing {filteredAwards.length} of {pagination.totalResults} awards
                            </span>
                        </div>
                    </div>

                    {loading && awards.length === 0 ? (
                        <div className="flex justify-center items-center py-20 bg-gray-800/50 rounded-2xl shadow-lg border border-gray-700/50">
                            <div className="text-center">
                                <FaSpinner className="animate-spin text-yellow-500 text-4xl mx-auto mb-4" />
                                <p className="text-gray-300 font-medium">Loading awards...</p>
                            </div>
                        </div>
                    ) : filteredAwards.length === 0 ? (
                        <div className="bg-gray-800/50 rounded-2xl shadow-lg border border-gray-700/50 p-12 text-center">
                            <FaTrophy className="text-5xl text-yellow-400/50 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-300 mb-2">
                                {loading ? "Loading..." : "No awards found"}
                            </h3>
                            <p className="text-gray-400 mb-6 max-w-md mx-auto">
                                {searchTerm || statusFilter !== "all"
                                    ? "No awards match your search criteria. Try adjusting your filters."
                                    : "No awards available at the moment. Check back later for exciting award announcements."}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredAwards.map((award) => {
                                const awardId = award._id || award.id;
                                const awardStatus = getAwardStatus(award);
                                const registrationStatus = getRegistrationStatus(award);
                                const StatusIcon = awardStatus.icon;
                                const RegistrationIcon = registrationStatus.icon;
                                const showApplyButton = isRegistrationOpen(award);

                                return (
                                    <motion.div
                                        key={awardId}
                                        className="bg-gray-800/50 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-700/50 overflow-hidden transition-all duration-300 hover:shadow-2xl flex flex-col"
                                        initial={{ opacity: 0, y: 50 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        viewport={{ once: true }}
                                    >
                                        {/* First Image - Clickable */}
                                        <div
                                            className="relative h-48 overflow-hidden cursor-pointer group"
                                            onClick={() => handleImageClick(award)}
                                        >
                                            <img
                                                src={award.firstImage}
                                                alt={award.awardFullName || "Award"}
                                                className="w-full h-full object-continer transition-transform duration-300 group-hover:scale-105"
                                                onError={(e) => {
                                                    e.target.src = "https://via.placeholder.com/400x200/6B7280/FFFFFF?text=Image+Not+Found";
                                                }}
                                            />

                                            {/* Click to view overlay */}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                <div className="bg-black/70 text-white px-3 py-1 rounded-full text-xs font-medium">
                                                    Click to view gallery
                                                </div>
                                            </div>

                                            {/* Award Status Badge */}
                                            <div className="absolute top-3 right-3">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 ${awardStatus.bgColor} ${awardStatus.textColor}`}>
                                                    <StatusIcon size={10} />
                                                    {awardStatus.label}
                                                </span>
                                            </div>

                                            {/* Registration Status Badge */}
                                            <div className="absolute top-12 right-3">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 border ${registrationStatus.bgColor} ${registrationStatus.textColor} ${registrationStatus.borderColor}`}>
                                                    <RegistrationIcon size={10} />
                                                    {registrationStatus.label}
                                                </span>
                                            </div>

                                            {/* Image Count Badge */}
                                            {award.allGalleryImages && award.allGalleryImages.length > 0 && (
                                                <div className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs">
                                                    {award.allGalleryImages.length} images
                                                </div>
                                            )}
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-6 flex-1 flex flex-col">
                                            {/* Award Name */}
                                            <h2 className="font-bold text-white mb-2 text-xl">
                                                {award.awardFullName || "Unnamed Award"}
                                            </h2>

                                            {/* Location */}
                                            <div className="mb-3 flex items-center gap-2 text-sm text-gray-300">
                                                <FaMapMarkerAlt className="text-yellow-500 flex-shrink-0" />
                                                <span className="line-clamp-1">{formatLocation(award)}</span>
                                            </div>

                                            {/* Award Description */}
                                            <p className="text-gray-300 mb-6 text-sm leading-relaxed flex-1">
                                                {award.awardDescription || "No description available."}
                                            </p>

                                            {/* Date Information */}
                                            <div className="space-y-3 border-t border-gray-700 pt-4 mb-4">
                                                {/* Event Start Date */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <FaCalendarAlt className="text-yellow-500 flex-shrink-0" />
                                                    <div>
                                                        <span className="text-gray-400">Event Starts:</span>
                                                        <span className="text-white ml-2">{formatDate(award.awardEventStartDate)}</span>
                                                    </div>
                                                </div>

                                                {/* Registration Period */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <FaCalendarAlt className="text-yellow-500 flex-shrink-0" />
                                                    <div>
                                                        <span className="text-gray-400">Registration:</span>
                                                        <span className="text-white ml-2">
                                                            {formatDate(award.registrationStartDate)} - {formatDate(award.registrationEndDate)}
                                                        </span>
                                                        {isRegistrationNotStarted(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-gray-600 text-gray-200 px-2 py-0.5 rounded-full text-xs">
                                                                <FaClock size={8} />
                                                                NOT STARTED
                                                            </span>
                                                        )}
                                                        {isRegistrationOpen(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 rounded-full text-xs">
                                                                <FaUserPlus size={8} />
                                                                OPEN
                                                            </span>
                                                        )}
                                                        {isRegistrationClosed(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs">
                                                                <FaTimesCircle size={8} />
                                                                CLOSED
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Event End Date */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <FaCalendarAlt className="text-yellow-500 flex-shrink-0" />
                                                    <div>
                                                        <span className="text-gray-400">Event Ends:</span>
                                                        <span className="text-white ml-2">{formatDate(award.awardEventEndDate)}</span>
                                                        {isAwardUpcoming(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs">
                                                                <FaClock size={8} />
                                                                UPCOMING
                                                            </span>
                                                        )}
                                                        {isAwardActive(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 rounded-full text-xs">
                                                                <FaUserClock size={8} />
                                                                ACTIVE
                                                            </span>
                                                        )}
                                                        {isAwardCompleted(award) && (
                                                            <span className="ml-2 inline-flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs">
                                                                <FaCheckCircle size={8} />
                                                                COMPLETED
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Apply Now Button - Only show when registration is open */}
                                            {showApplyButton && (
                                                <motion.button
                                                    onClick={() => handleApplyNow(award)}
                                                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-gray-900 font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <FaExternalLinkAlt size={14} />
                                                    Apply Now
                                                </motion.button>
                                            )}

                                            {/* Registration Not Started Message */}
                                            {isRegistrationNotStarted(award) && (
                                                <div className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium py-3 px-4 rounded-xl text-center">
                                                    Registration Starts {formatDate(award.registrationStartDate)}
                                                </div>
                                            )}

                                            {/* Registration Closed Message */}
                                            {isRegistrationClosed(award) && !isAwardCompleted(award) && (
                                                <div className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-medium py-3 px-4 rounded-xl text-center">
                                                    Registration Closed
                                                </div>
                                            )}

                                            {/* Completed Event Message */}
                                            {isAwardCompleted(award) && (
                                                <div className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-gray-200 font-medium py-3 px-4 rounded-xl text-center">
                                                    Event Completed
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination Info */}
                    {pagination.totalPages > 1 && (
                        <div className="mt-8 bg-gray-800/50 rounded-2xl shadow-lg border border-gray-700/50 p-4 text-center">
                            <p className="text-gray-300 text-sm">
                                Page {pagination.page} of {pagination.totalPages} •
                                Showing {awards.length} awards per page •
                                Total {pagination.totalResults} awards
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Image Popup Modal */}
            <AnimatePresence>
                {isPopupOpen && selectedAward && allImages.length > 0 && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closePopup}
                    >
                        {/* Close Button */}
                        <button
                            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/80 rounded-full p-2 z-10"
                            onClick={closePopup}
                        >
                            <FaTimes size={24} />
                        </button>

                        {/* Navigation Buttons */}
                        <button
                            className="absolute left-4 text-white bg-black/50 hover:bg-black/80 rounded-full p-3 z-10"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                        >
                            <FaArrowLeft size={20} />
                        </button>

                        <button
                            className="absolute right-4 text-white bg-black/50 hover:bg-black/80 rounded-full p-3 z-10"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                        >
                            <FaArrowRight size={20} />
                        </button>

                        {/* Image Container */}
                        <motion.div
                            className="relative max-w-4xl max-h-[80vh] mx-4"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Current Image */}
                            <img
                                src={allImages[selectedImageIndex]}
                                alt={`${selectedAward.awardFullName} - Image ${selectedImageIndex + 1}`}
                                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                            />

                            {/* Image Info */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 text-white">
                                <h3 className="text-lg font-bold">{selectedAward.awardFullName}</h3>
                                <p className="text-sm opacity-90">
                                    Image {selectedImageIndex + 1} of {allImages.length}
                                </p>
                            </div>

                            {/* Image Navigation Dots */}
                            {allImages.length > 1 && (
                                <div className="flex justify-center mt-4 space-x-2">
                                    {allImages.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`w-3 h-3 rounded-full ${index === selectedImageIndex
                                                    ? 'bg-yellow-500'
                                                    : 'bg-gray-400 hover:bg-gray-300'
                                                }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedImageIndex(index);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </motion.div>

                        {/* Keyboard Instructions */}
                        <div className="absolute bottom-4 left-0 right-0 text-center text-gray-400 text-sm">
                            Use ← → arrow keys to navigate • Press ESC to close
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default AwardsGallery;
